import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const CONSENT_KEY = "invyt_cookie_consent";

// Lightweight cookie-consent banner. Shows once until the visitor makes a choice
// (stored in localStorage). Invyt only uses essential cookies today, so this is a
// notice + acknowledge pattern with a link to the policy.
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(CONSENT_KEY)) setVisible(true);
    } catch {
      // localStorage unavailable (private mode) — don't nag.
    }
  }, []);

  const choose = (value) => {
    try { localStorage.setItem(CONSENT_KEY, value); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-40 p-2.5 sm:p-3"
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
    >
      <div className="max-w-2xl mx-auto rounded-2xl bg-white/60 backdrop-blur-xl text-slate-700 shadow-lg ring-1 ring-white/50 border border-white/40 px-3.5 py-2.5 flex items-center gap-3">
        <div className="w-7 h-7 rounded-lg bg-white/60 flex items-center justify-center shrink-0">
          <Cookie className="w-4 h-4 text-[#1E2019]" />
        </div>
        <p className="text-xs text-slate-600 leading-snug min-w-0 flex-1">
          We use essential cookies to make Invyt work.{" "}
          <Link to="/privacy" className="underline text-slate-800 hover:text-[#1E2019] transition-colors" data-testid="cookie-policy-link">
            Learn more
          </Link>
        </p>
        <button
          onClick={() => choose("essential")}
          data-testid="cookie-essential-btn"
          className="hidden sm:inline-flex h-7 px-3 rounded-full text-xs font-semibold text-slate-500 hover:text-slate-800 hover:bg-white/60 transition-colors whitespace-nowrap"
        >
          Essential only
        </button>
        <button
          onClick={() => choose("accepted")}
          data-testid="cookie-accept-btn"
          className="h-7 px-4 rounded-full bg-[#1E2019] hover:bg-[#1E2019] text-white text-xs font-semibold transition-colors whitespace-nowrap shrink-0"
        >
          Accept
        </button>
        <button
          onClick={() => choose("essential")}
          aria-label="Dismiss"
          data-testid="cookie-dismiss-btn"
          className="w-7 h-7 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-white/60 transition-colors shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
